// Deterministic A/B bucket from pblk_sid, cached for the session lifetime.
// Same sid → same bucket on every call, so quiz_start and downstream events
// land in the same variant cohort in GA4 / ClickHouse.
//
// Minimal-by-design: no per-quiz config, no sticky returning-user variants,
// no API-driven selection. Issue #35 unblocks measurement with a simple hash;
// publinker#628 will later override this with a variant editor.
//
// DEV-1135 adds deterministic FORCING via the `?pblk_variant=A|B` query param
// so deploy validation and E2E smokes can exercise a specific variant. Forced
// sessions are stamped assignment_source='forced' in the event context and
// MUST be excluded from experiment analysis.
//
// DEV-1737 adds a CONFIGURABLE split: `quiz.variant_b_pct` (integer 0–100, the
// share of hashed sessions bucketed into B; edited in the Publinker quiz form).
// Two hash paths on purpose:
//   - pct absent/invalid → the original charCode-parity 50/50, byte-identical
//     to every bundle deployed before this change, so a running 50/50 test keeps
//     its assignment even if its site is redeployed mid-test.
//   - pct present → FNV-1a(sid) % 100 < pct. Parity can't express 90/10, and
//     a charCode SUM mod 100 is not uniform over UUID-shaped sids.
// ClickHouse reproduces the parity path as
//   arraySum(arrayMap(c -> reinterpretAsUInt8(c), splitByString('', session_id))) % 2
// (0 → A); the FNV path is stamped as `variant_b_pct` on every event instead.

export type VariantId = 'A' | 'B' | 'control';
export type AssignmentSource = 'hash' | 'forced' | 'control';

export const DEFAULT_VARIANT_B_PCT = 50;

let cachedVariantId: VariantId | null = null;
let cachedAssignmentSource: AssignmentSource = 'hash';
let cachedVariantBPct: number = DEFAULT_VARIANT_B_PCT;

/** Integer 0–100 from a raw config value, or null when absent/unusable. */
export function normalizeVariantBPct(raw: unknown): number | null {
  const n = typeof raw === 'string' && raw.trim() !== '' ? Number(raw) : raw;
  if (typeof n !== 'number' || !Number.isFinite(n)) return null;
  const pct = Math.round(n);
  return pct >= 0 && pct <= 100 ? pct : null;
}

function fnv1a32(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

function readForcedVariant(): VariantId | null {
  try {
    const forced = new URLSearchParams(window.location.search).get('pblk_variant');
    if (forced === 'A' || forced === 'B') return forced;
  } catch {
    // SSR / test environments without window — fall through to hash bucketing.
  }
  return null;
}

/**
 * Bucket the session. The FIRST call decides and is cached for the session
 * lifetime, so the store passes `variant_b_pct` on that first call (right
 * after the config loads and the external sid resolves); later callers (event
 * context, lead payload) just read the cache and may omit the second argument.
 */
export function getVariantIdForSession(pblkSid: string | undefined, variantBPct?: unknown): VariantId {
  if (cachedVariantId) return cachedVariantId;

  const pct = normalizeVariantBPct(variantBPct);
  cachedVariantBPct = pct ?? DEFAULT_VARIANT_B_PCT;

  const forced = readForcedVariant();
  if (forced) {
    cachedVariantId = forced;
    cachedAssignmentSource = 'forced';
    return cachedVariantId;
  }

  if (!pblkSid) {
    cachedVariantId = 'control';
    cachedAssignmentSource = 'control';
    return cachedVariantId;
  }
  if (pct === null) {
    let sum = 0;
    for (let i = 0; i < pblkSid.length; i++) sum += pblkSid.charCodeAt(i);
    cachedVariantId = sum % 2 === 0 ? 'A' : 'B';
  } else {
    cachedVariantId = fnv1a32(pblkSid) % 100 < pct ? 'B' : 'A';
  }
  cachedAssignmentSource = 'hash';
  return cachedVariantId;
}

export function getAssignmentSource(): AssignmentSource {
  return cachedAssignmentSource;
}

/** Effective B share (0–100) the session was bucketed under; 50 when unconfigured. */
export function getVariantBPct(): number {
  return cachedVariantBPct;
}

export function __resetVariantCacheForTests(): void {
  cachedVariantId = null;
  cachedAssignmentSource = 'hash';
  cachedVariantBPct = DEFAULT_VARIANT_B_PCT;
}
